import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, RefreshCw, Play, Pause } from "lucide-react";
import { useLang } from "@/context/LangContext";

// A simple voice recorder — records mic audio to a blob and previews it.
export const SimpleRecorder = ({ onRecorded }) => {
  const { t } = useLang();
  const [phase, setPhase] = useState("idle"); // idle | recording | recorded
  const [url, setUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setUrl(URL.createObjectURL(blob));
        onRecorded(blob);
        setPhase("recorded");
      };
      recRef.current = rec;
      rec.start();
      setPhase("recording");
    } catch (e) {
      toast.error(t("mic_denied"));
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const reset = () => { setPhase("idle"); setUrl(null); onRecorded(null); };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="simple-recorder">
      {phase === "idle" && (
        <>
          <button onClick={start} data-testid="voice-record-start" className="w-full h-16 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
            <Mic size={24} /> {t("voice_record_hint")}
          </button>
        </>
      )}
      {phase === "recording" && (
        <button onClick={stop} data-testid="voice-record-stop" className="w-full h-16 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2 sk-glow" style={{ background: "#dc2626", color: "#fff" }}>
          <Square size={20} fill="currentColor" /> {t("ws_stop")} · {t("recording_now")}
        </button>
      )}
      {phase === "recorded" && (
        <div className="flex items-center gap-3">
          <button onClick={toggle} data-testid="voice-preview" className="grid place-items-center w-14 h-14 rounded-full shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
            {playing ? <Pause size={22} /> : <Play size={22} fill="currentColor" />}
          </button>
          <span className="font-ui text-sm font-semibold flex-1" style={{ color: "var(--text-2)" }}>✓ {t("voice_created")}</span>
          <button onClick={reset} data-testid="voice-rerecord" className="grid place-items-center w-12 h-12 rounded-full shrink-0" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
            <RefreshCw size={18} />
          </button>
          <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} className="hidden" />
        </div>
      )}
    </div>
  );
};
